/**
 * MSAL Authentication Configuration
 * Azure AD / Entra ID integration for external-intake.
 *
 * Adapted from aws-ai-monitoring-app/frontend/src/config/auth.ts.
 * Key differences from the reference:
 *   - loginRequest scopes limited to openid/profile/email (no User.Read Graph scope).
 *   - acquireToken only acquires the API scope.
 *   - acquireGraphToken uses popup (not redirect) since it's a secondary, non-critical scope.
 */

import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';

/**
 * MSAL configuration object.
 * Values come from Vite env vars; see frontend/.env.dev.example for names.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes for the interactive login request.
 * openid/profile/email are the minimal OIDC scopes; User.Read lets us read the
 * signed-in user's own profile (jobTitle/department) via Graph /me.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes for acquiring an API access token.
 * The backend validates this audience against VITE_API_CLIENT_ID.
 */
export const apiRequest = {
  scopes: [`api://${import.meta.env.VITE_API_CLIENT_ID || ''}/access_as_user`],
};

/**
 * Scopes for Microsoft Graph — used only to fetch the signed-in user's profile photo.
 * No admin consent required for User.ReadBasic.All.
 */
export const graphRequest = {
  scopes: ['User.ReadBasic.All'],
};

/**
 * MSAL singleton — the one instance used throughout the app.
 * Import this everywhere instead of constructing a new PublicClientApplication.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Track redirect attempts to prevent infinite loops.
const REDIRECT_KEY = 'msal_redirect_attempt';
const MAX_REDIRECT_ATTEMPTS = 2;

/**
 * Initialize the MSAL instance and handle any in-flight redirect.
 * Must be called (and awaited) before the React tree renders.
 * F4 calls this in main.tsx.
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();

  // Handle redirect promise from a prior acquireTokenRedirect call.
  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
  }

  // Restore the active account if the page reloaded.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

/**
 * Acquire a Microsoft Graph token silently; fall back to popup on failure.
 * Uses popup (not redirect) because Graph access is non-critical UX — a
 * failed photo load degrades to initials, it does not break the page.
 * Returns null on any failure so callers can safely ignore it.
 */
export async function acquireGraphToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  } catch {
    try {
      const response = await msalInstance.acquireTokenPopup({ ...graphRequest, account });
      return response.accessToken;
    } catch {
      return null;
    }
  }
}

/**
 * Acquire an API access token silently; fall back to redirect on failure.
 * Returns null if no account is signed in (caller should trigger login).
 * The API client (F2) calls this before every authenticated request.
 */
export async function acquireToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
    });
    // Clear redirect counter on success.
    sessionStorage.removeItem(REDIRECT_KEY);
    return response.accessToken;
  } catch {
    const attempts = parseInt(sessionStorage.getItem(REDIRECT_KEY) || '0', 10);

    if (attempts >= MAX_REDIRECT_ATTEMPTS) {
      console.error('Max redirect attempts reached. API scope may not be configured in Azure AD.');
      sessionStorage.removeItem(REDIRECT_KEY);
      return null;
    }

    sessionStorage.setItem(REDIRECT_KEY, String(attempts + 1));
    console.warn(`Silent token acquisition failed (attempt ${attempts + 1}), redirecting to login…`);
    await msalInstance.acquireTokenRedirect(apiRequest);
    // Not reached — redirect navigates away.
    return null;
  }
}
